import { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface Props {
  /** 唯一識別碼，用 localStorage 記住摺疊狀態 */
  storageKey?: string;
  /** 預設摺起？ */
  defaultCollapsed?: boolean;
  /** 標題 */
  title: ReactNode;
  /** 標題旁的描述 / 數字徽章 */
  subtitle?: ReactNode;
  /** 標題右側額外控制（如 refresh 按鈕） */
  actions?: ReactNode;
  /** 內容 */
  children: ReactNode;
}

/**
 * 可摺疊區塊。用 localStorage 記住使用者偏好。
 * 主要用來讓「次級面板」預設摺起來避免主畫面太擁擠。
 */
export function CollapsibleSection({
  storageKey,
  defaultCollapsed = true,
  title,
  subtitle,
  actions,
  children,
}: Props) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (!storageKey) return defaultCollapsed;
    try {
      const v = localStorage.getItem(`collapsible:${storageKey}`);
      if (v === '1') return true;
      if (v === '0') return false;
    } catch { /**/ }
    return defaultCollapsed;
  });

  function toggle() {
    const next = !collapsed;
    setCollapsed(next);
    if (storageKey) {
      try { localStorage.setItem(`collapsible:${storageKey}`, next ? '1' : '0'); } catch { /**/ }
    }
  }

  return (
    <section className="card p-0 overflow-hidden">
      <header
        onClick={toggle}
        className="flex items-center justify-between gap-2 px-3 py-2 cursor-pointer hover:bg-(--bg-soft) transition-colors select-none"
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {collapsed ? <ChevronRight size={14} className="text-(--text-muted) shrink-0" /> : <ChevronDown size={14} className="text-(--accent) shrink-0" />}
          <span className="font-semibold text-sm text-(--text-primary)">{title}</span>
          {subtitle && <span className="text-[10px] text-(--text-muted) truncate">{subtitle}</span>}
        </div>
        {actions && (
          <div onClick={(e) => e.stopPropagation()} className="shrink-0">
            {actions}
          </div>
        )}
      </header>
      {!collapsed && (
        <div className="px-3 pb-3 pt-1 border-t border-(--border)">
          {children}
        </div>
      )}
    </section>
  );
}
